import { useCallback, useEffect, useState } from "react";
import type { DialogueLine } from "./act1.ts";
import { OptionsButton } from "./OptionsButton.tsx";
import { dName, loc, type Loc } from "./loc.ts";
import { useGt } from "./gameText.ts";
import { useLang } from "../../gamzone/i18n.ts";

const DEFAULT_END_LABEL: Loc = { ko: "결투 시작", en: "Start duel" };

/**
 * A dialogue scene: a background, one character illustration, and a bottom
 * panel advanced one line at a time (tap/click/Enter). `portraits` maps a
 * speaker name → illustration; the shown portrait follows the current speaker
 * and, on a line with no portrait (the player), holds the last one. No fixed
 * protagonist face. The last line runs `onStart` instead of advancing;
 * `endLabel` is its hint text (e.g. "결투 시작", "지도로").
 */
export function DialogueScene({
  lines,
  bg,
  portraits,
  onStart,
  endLabel = DEFAULT_END_LABEL,
}: {
  lines: readonly DialogueLine[];
  bg: string;
  portraits: Record<string, string>;
  onStart: () => void;
  endLabel?: Loc;
}) {
  const lang = useLang();
  const gt = useGt();
  const [i, setI] = useState(0);
  const last = i >= lines.length - 1;
  const line = lines[i];

  const advance = useCallback(() => {
    setI((n) => Math.min(n + 1, lines.length - 1));
  }, [lines.length]);

  const act = last ? onStart : advance;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        act();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [act]);

  // Current speaker's portrait, or the most recent one if this line has none.
  let portrait: string | undefined;
  for (let k = i; k >= 0; k--) {
    const p = portraits[lines[k].name];
    if (p) {
      portrait = p;
      break;
    }
  }

  return (
    <div
      className="cam-screen cam-dialogue"
      style={{ ["--cam-bg" as string]: `url(${bg})` }}
    >
      <OptionsButton />

      {portrait && (
        <img
          key={portrait}
          className="cam-dialogue-portrait"
          src={portrait}
          alt=""
          aria-hidden
        />
      )}

      <div
        className="cam-dialogue-panel"
        role="button"
        tabIndex={0}
        aria-label={last ? loc(endLabel, lang) : gt("nextLine")}
        onClick={act}
      >
        <span className={`cam-speaker ${line.speaker}`}>{dName(line.name, lang)}</span>
        <p key={i} className="cam-dialogue-line">
          {loc(line.text, lang)}
        </p>
        <span className="cam-hint">{last ? loc(endLabel, lang) : gt("tapContinue")}</span>
      </div>
    </div>
  );
}
